import { Component } from '@theme/component';

// Store handles only. Cards and prices are rendered anew in the current market.
class AtelierRecent extends Component {
  #observer; #listeners; #request; #handles = [];
  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    this.querySelector('[data-recent-retry]').addEventListener('click', () => this.#load(), { signal: this.#listeners.signal });
    try {
      const valid = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
      const stored = localStorage.getItem('fivepoint-atelier-recent-v1');
      const history = stored === null ? [] : JSON.parse(stored);
      if (!valid(this.dataset.handle) || !Array.isArray(history) || history.length > 9 || !history.every(valid)) throw new Error('Invalid recent references');
      this.#handles = [...new Set(history)].filter(handle => handle !== this.dataset.handle).slice(0, 8);
      localStorage.setItem('fivepoint-atelier-recent-v1', JSON.stringify([this.dataset.handle, ...this.#handles]));
      if (!this.#handles.length) return;
      this.hidden = false;
      this.querySelector('[data-recent-status]').textContent = this.dataset.loading;
      this.#observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.#observer.disconnect(); this.#load(); }
      }, { rootMargin: '200px' });
      this.#observer.observe(this);
    } catch { this.#error(false); }
  }
  #error(retry) {
    this.hidden = false;
    this.querySelector('[data-recent-status]').textContent = this.dataset.error;
    this.querySelector('[data-recent-retry]').hidden = !retry;
  }
  async #load() {
    this.#request?.abort();
    this.#request = new AbortController();
    const signal = AbortSignal.any([this.#request.signal, AbortSignal.timeout(10000)]);
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-recent-status]').textContent = this.dataset.loading;
    this.querySelector('[data-recent-retry]').hidden = true;
    try {
      const { root, market, locale, currency } = this.dataset;
      if (!root?.startsWith('/') || root.startsWith('//') || !market || !locale || !currency) throw new Error('Invalid recent context');
      const url = new URL(root.replace(/\/$/, '') + '/search', location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid recent origin');
      url.searchParams.set('section_id', 'fivepoint-atelier-recent');
      url.searchParams.set('q', this.#handles.join('|'));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
      if (!response.ok) throw new Error('Recent products could not be loaded');
      const html = await response.text();
      if (html.length > 1048576) throw new Error('Invalid recent response');
      const batch = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-atelier-recent-batch]');
      if (!batch || batch.dataset.market !== market || batch.dataset.locale !== locale || batch.dataset.currency !== currency) throw new Error('Recent context changed');
      const results = [...batch.querySelectorAll('[data-recent-handle]')];
      if (results.length !== this.#handles.length || results.some((result, index) => result.dataset.recentHandle !== this.#handles[index] || !['available', 'unavailable', 'error'].includes(result.dataset.recentState))) throw new Error('Invalid recent response');
      if (signal.aborted || !this.isConnected) return;
      // A market exclusion is a normal result. Keep the handle in history so
      // switching to another market can resolve it again without a stale card.
      const cards = results.filter(result => result.dataset.recentState === 'available');
      const hasError = results.some(result => result.dataset.recentState === 'error');
      const track = this.querySelector('[data-fp-carousel-track]');
      track.replaceChildren(...cards);
      document.dispatchEvent(new CustomEvent('atelier:catalog-updated'));
      track.dispatchEvent(new Event('scroll'));
      this.hidden = cards.length === 0 && !hasError;
      if (hasError) this.#error(true);
      else this.querySelector('[data-recent-status]').textContent = '';
    } catch { if (!this.#request.signal.aborted) this.#error(true); }
    finally { this.removeAttribute('aria-busy'); }
  }
  disconnectedCallback() {
    super.disconnectedCallback(); this.#observer?.disconnect(); this.#listeners?.abort(); this.#request?.abort();
  }
}
if (!customElements.get('fivepoint-atelier-recent')) customElements.define('fivepoint-atelier-recent', AtelierRecent);
